/**
 * ASCII Hero Engine - AsciiParticle Class
 * Individual Text Grid Particle with Eased Spotlight, Staggered Delay & Glow Wake
 */

import { Easings } from "../utils/easings.js";

export class AsciiParticle {
  constructor(gridX, gridY, brightness, char, isMidGrey = false) {
    this.originX = gridX;
    this.originY = gridY;
    this.x = gridX;
    this.y = gridY;

    this.brightness = brightness;
    this.normB = brightness / 255;
    this.depth = 0.2 + this.normB * 0.8;
    this.isMidGrey = isMidGrey;

    this.baseChar = char;
    this.char = char;

    // Atmospheric ambient base alpha (moody portrait silhouette)
    this.baseAlpha = isMidGrey ? 0.26 + this.normB * 0.46 : 0.12 + this.normB * 0.62;
    this.alpha = this.baseAlpha;
    this.spotlightIntensity = 0;

    this.glitchTimer = 0;
    this.glitchColor = null;
  }

  update(mouse, config, parallaxX = 0, parallaxY = 0) {
    // Staggered 3D Depth Parallax with weighted delay per depth plane
    const targetX = this.originX + parallaxX * this.depth;
    const targetY = this.originY + parallaxY * this.depth;

    const depthLag = 0.08 + this.depth * 0.06;
    this.x += (targetX - this.x) * depthLag;
    this.y += (targetY - this.y) * depthLag;

    // Distance from particle to smooth spotlight cursor
    const dx = this.x - (mouse ? mouse.x : -9999);
    const dy = this.y - (mouse ? mouse.y : -9999);
    const dist = mouse && mouse.x !== -9999 ? Math.sqrt(dx * dx + dy * dy) : 99999;

    const sRadius = (config && config.spotlightRadius) || 280;
    const sPower = config && config.spotlightPower !== undefined ? config.spotlightPower : 1.0;

    let targetSpotlight = 0;
    if (mouse && mouse.x !== -9999 && dist < sRadius) {
      const normDist = dist / sRadius;
      const lightCurve = Easings.easeOutQuart(1 - normDist);
      targetSpotlight = lightCurve * sPower;
    }

    // Asymmetric Attack & Release with Eased Delay
    if (targetSpotlight > this.spotlightIntensity) {
      const attackSpeed = (config && config.spotlightAttackSpeed) || 0.16;
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * attackSpeed;
    } else {
      const decaySpeed = (config && config.spotlightDecaySpeed) || 0.048;
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * decaySpeed;
    }

    const easedIntensity = Easings.easeOutCubic(Math.min(1.0, this.spotlightIntensity));
    const targetAlpha = Math.min(1.0, this.baseAlpha + easedIntensity * (0.88 - this.baseAlpha * 0.25));
    this.alpha += (targetAlpha - this.alpha) * 0.14;

    // Dynamic typographic density & detail reveal under the spotlight
    if (this.spotlightIntensity > 0.03) {
      const effectiveLum = Math.min(255, this.brightness * (1.0 + easedIntensity * 0.55) + easedIntensity * 32);

      if (this.isMidGrey) {
        const ramp = config.midGreyRamp;
        const idx = Math.min(ramp.length - 1, Math.max(0, Math.floor((effectiveLum / 255) * ramp.length)));
        this.char = ramp[idx];
      } else {
        const ramp = config.baseRamp;
        const idx = Math.min(ramp.length - 1, Math.max(1, Math.floor((effectiveLum / 255) * ramp.length)));
        this.char = ramp[idx];
      }

      // Subtle micro-glitch shimmer at the center of the beam
      if (this.spotlightIntensity > 0.58 && Math.random() < 0.025 * this.spotlightIntensity) {
        const pool = this.isMidGrey ? config.midGreyGlitchChars : config.baseGlitchChars;
        this.char = pool[Math.floor(Math.random() * pool.length)];
        this.glitchTimer = 6;
        if (config && config.glitchColorsRGB && Math.random() < (config.glitchColorChance || 0.35)) {
          this.glitchColor = config.glitchColorsRGB[Math.floor(Math.random() * config.glitchColorsRGB.length)];
        }
      }
    } else {
      if (this.glitchTimer > 0) {
        this.glitchTimer--;
      } else {
        this.char = this.baseChar;
        this.glitchColor = null;
      }
    }

    if (this.glitchTimer > 0) {
      this.glitchTimer--;
      if (this.glitchTimer <= 0) {
        this.glitchColor = null;
      }
    }
  }

  draw(ctx, config, globalAlpha = 1.0) {
    const effectiveAlpha = this.alpha * globalAlpha;
    if (effectiveAlpha <= 0.001) return;

    if (this.glitchColor) {
      const [r, g, b] = this.glitchColor;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${Math.min(1, effectiveAlpha * 1.3)})`;
    } else if (this.isMidGrey) {
      const [r, g, b] = (config && config.midGreyColorRGB) || [68, 68, 68];
      if (this.spotlightIntensity > 0.04) {
        const boost = Math.min(1, this.spotlightIntensity * 0.7);
        const nr = Math.round(r + (255 - r) * boost);
        const ng = Math.round(g + (255 - g) * boost);
        const nb = Math.round(b + (255 - b) * boost);
        ctx.fillStyle = `rgba(${nr}, ${ng}, ${nb}, ${effectiveAlpha})`;
      } else {
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${effectiveAlpha})`;
      }
    } else {
      ctx.fillStyle = `rgba(255, 255, 255, ${effectiveAlpha})`;
    }
    ctx.fillText(this.char, this.x, this.y);
  }
}
